// Desk Inventory: materials/receive/recipe hooks + Stock|Missing surfaces.
import { useCallback, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import {
  canDeactivateManufacturingModel,
  canEditManufacturingCatalogCode,
  canPermanentlyDeleteManufacturingMaterial,
} from "@/lib/manufacturing/modelDeleteAccess";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import { ManufacturingGateBanner, ManufacturingToast } from "./ManufacturingGateBanner";
import {
  ManufacturingInventoryDeskHeader,
  type InventoryDeskSurface,
} from "./ManufacturingInventoryDeskHeader";
import { ManufacturingInventoryDeskStockBody } from "./ManufacturingInventoryDeskStockBody";
import { ManufacturingInventoryMissingBody } from "./ManufacturingInventoryMissingBody";
import { ManufacturingInventoryPanelModals } from "./ManufacturingInventoryPanelModals";
import { ManufacturingInventoryPanelHosts } from "./ManufacturingInventoryPanelHosts";
import { ManufacturingInventoryMaterialDrawer } from "./ManufacturingInventoryMaterialDrawer";
import { useAutoDismissToast } from "./useAutoDismissToast";
import {
  useManufacturingCreateMaterial,
  type CreateMaterialPayload,
} from "./useManufacturingCreateMaterial";
import { useManufacturingEntitlementGate } from "./useManufacturingEntitlementGate";
import { useManufacturingMaterials } from "./useManufacturingMaterials";
import { useManufacturingMaterialsRecipeUsage } from "./useManufacturingMaterialsRecipeUsage";
import { useManufacturingReceiveLines } from "./useManufacturingReceiveLines";
import { useManufacturingReceiptsAttention } from "./useManufacturingReceiptsAttention";
import { useManufacturingRecipeInventory } from "./useManufacturingRecipeInventory";
import { useManufacturingRecipesList } from "./useManufacturingRecipesList";
import { useManufacturingInventoryCleanup } from "./useManufacturingInventoryCleanup";
import { useManufacturingInventoryDeletions } from "./useManufacturingInventoryDeletions";
import { useManufacturingInventoryDeskFilters } from "./useManufacturingInventoryDeskFilters";
import { useManufacturingMissingCount } from "./useManufacturingMissingCount";
import { useManufacturingUpdateMaterial } from "./useManufacturingUpdateMaterial";
import type { InventoryViewTab, PostingTab } from "./manufacturingInventoryTabs";
import type { MfgModalKind } from "./mfgPanelTypes";

export function ManufacturingInventoryDeskPanel({
  onOpenInventoryCount,
  onOpenPurchasing,
}: {
  onOpenInventoryCount?: () => void;
  onOpenPurchasing?: () => void;
}) {
  const auth = useAuth();
  const gate = useManufacturingEntitlementGate();
  const canDeactivateModel =
    auth?.status === "ready" && canDeactivateManufacturingModel((...r) => auth.hasRole(...r));
  const canEditCatalogCode =
    auth?.status === "ready" && canEditManufacturingCatalogCode((...r) => auth.hasRole(...r));
  const canPermanentDeleteMaterial =
    auth?.status === "ready" &&
    canPermanentlyDeleteManufacturingMaterial((...r) => auth.hasRole(...r));
  const materials = useManufacturingMaterials({ enabled: gate.enabled });
  const recipeUsage = useManufacturingMaterialsRecipeUsage({ enabled: gate.enabled });
  const recipes = useManufacturingRecipesList({ enabled: gate.enabled });
  const recipeInventory = useManufacturingRecipeInventory({ enabled: gate.enabled });
  const receiptsAttention = useManufacturingReceiptsAttention({ enabled: gate.enabled });
  const receive = useManufacturingReceiveLines({
    onPosted: materials.refresh,
    materials: materials.materials,
  });
  const createMaterial = useManufacturingCreateMaterial({ onCreated: materials.refresh });
  const updateMaterial = useManufacturingUpdateMaterial({ onUpdated: materials.refresh });
  const missingCount = useManufacturingMissingCount(gate.enabled);

  const [deskSurface, setDeskSurface] = useState<InventoryDeskSurface>("stock");
  const [openModal, setOpenModal] = useState<MfgModalKind | null>(null);
  const [editingMaterial, setEditingMaterial] = useState<MfgMaterial | undefined>(undefined);
  const [stockMaterial, setStockMaterial] = useState<MfgMaterial | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [templateConfirmOpen, setTemplateConfirmOpen] = useState(false);
  const [importToast, setImportToast] = useState<string | null>(null);
  const [recipeToast, setRecipeToast] = useState<string | null>(null);
  const [inventoryTab, setInventoryTab] = useState<InventoryViewTab>("materials");
  const [postingTab, setPostingTab] = useState<PostingTab>("receive");
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [recipeDupsOpen, setRecipeDupsOpen] = useState(false);
  const [sdsBookOpen, setSdsBookOpen] = useState(false);
  const [sdsMaterial, setSdsMaterial] = useState<MfgMaterial | null>(null);
  const cleanup = useManufacturingInventoryCleanup({
    refreshMaterials: materials.refresh,
    refreshRecipes: () => {
      recipes.refresh();
      recipeInventory.refresh();
    },
  });
  const deletions = useManufacturingInventoryDeletions({
    cleanup,
    refreshMaterials: materials.refresh,
    onSummaryToast: (msg) => setImportToast(msg),
  });

  const deskFilters = useManufacturingInventoryDeskFilters(
    materials.materials,
    recipeUsage.usedMaterialIds,
    recipeUsage.ready,
  );
  const {
    categories,
    existingUnits,
    existingShippingUnits,
    filtered,
  } = deskFilters;

  useAutoDismissToast(createMaterial.toast, createMaterial.dismissToast);
  useAutoDismissToast(updateMaterial.toast, updateMaterial.dismissToast);
  useAutoDismissToast(receive.toast, receive.dismissToast);
  useAutoDismissToast(importToast, useCallback(() => setImportToast(null), []), 4000);
  useAutoDismissToast(recipeToast, useCallback(() => setRecipeToast(null), []), 4000);

  const handleCreate = useCallback(
    async (payload: CreateMaterialPayload) => {
      const r = await createMaterial.submit(payload);
      if (r.ok) return { ok: true } as const;
      if (r.duplicate) {
        setEditingMaterial(r.duplicate);
        return {
          ok: false as const,
          error: `"${r.duplicate.name}" already exists — opened for editing.`,
        };
      }
      return { ok: false as const, error: r.error };
    },
    [createMaterial]
  );

  function openCreateModal() {
    setEditingMaterial(undefined);
    setOpenModal("material");
  }

  function openEditModal(m: MfgMaterial) {
    setEditingMaterial(m);
    setOpenModal("material");
  }

  function closeModal() {
    setOpenModal(null);
    setEditingMaterial(undefined);
  }

  const busy = gate.loading || gate.working || materials.loading || materials.working;

  return (
    <div
      className="w-full text-left space-y-4"
      {...(gate.enabled ? { [FIREFLY_TARGET_ATTR]: "mfg-inventory-surface" } : {})}
    >
      <ManufacturingInventoryDeskHeader
        busy={busy}
        refreshing={materials.working}
        activeSurface={deskSurface}
        onSurfaceChange={setDeskSurface}
        missingCount={missingCount.total}
        onRefresh={() => {
          gate.refresh();
          materials.refresh();
          void recipeUsage.refresh();
          recipeInventory.refresh();
          void missingCount.reload();
        }}
        onOpenInventoryCount={onOpenInventoryCount}
        onOpenPurchasing={onOpenPurchasing}
      />

      <ManufacturingToast message={createMaterial.toast} />
      <ManufacturingToast message={updateMaterial.toast} />
      <ManufacturingToast message={receive.toast} />
      <ManufacturingToast message={importToast} />
      <ManufacturingToast message={recipeToast} />
      <ManufacturingGateBanner gate={gate} />
      {gate.enabled && materials.error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {materials.error}
        </div>
      ) : null}

      {gate.enabled && deskSurface === "missing" ? (
        <ManufacturingInventoryMissingBody
          enabled={gate.enabled}
          materials={materials.materials}
          onChanged={() => {
            void missingCount.reload();
            materials.refresh();
          }}
        />
      ) : null}

      {gate.enabled && deskSurface === "stock" ? (
        <ManufacturingInventoryDeskStockBody
          search={deskFilters.search}
          onSearchChange={deskFilters.setSearch}
          category={deskFilters.category}
          onCategoryChange={deskFilters.setCategory}
          categories={categories}
          activeFilter={deskFilters.activeFilter}
          onActiveFilterChange={deskFilters.setActiveFilter}
          msdsStatusFilter={deskFilters.msdsStatusFilter}
          onMsdsStatusFilterChange={deskFilters.setMsdsStatusFilter}
          recipeUsageFilter={deskFilters.recipeUsageFilter}
          onRecipeUsageFilterChange={deskFilters.setRecipeUsageFilter}
          qtyFilter={deskFilters.qtyFilter}
          onQtyFilterChange={deskFilters.setQtyFilter}
          priceFilter={deskFilters.priceFilter}
          onPriceFilterChange={deskFilters.setPriceFilter}
          filtered={filtered}
          materials={materials.materials}
          inventoryValue={materials.inventoryValue}
          costingLastUpdatedAt={materials.costingLastUpdatedAt}
          busy={busy}
          gateEnabled={gate.enabled}
          inventoryTab={inventoryTab}
          onInventoryTabChange={setInventoryTab}
          postingTab={postingTab}
          onPostingTabChange={setPostingTab}
          duplicatesOpen={duplicatesOpen}
          onToggleDuplicates={() => setDuplicatesOpen((v) => !v)}
          onCloseDuplicates={() => setDuplicatesOpen(false)}
          recipeDupsOpen={recipeDupsOpen}
          onToggleRecipeDups={() => setRecipeDupsOpen((v) => !v)}
          onCloseRecipeDups={() => setRecipeDupsOpen(false)}
          onCreate={openCreateModal}
          onEdit={openEditModal}
          onOpenTransactions={(m) => setStockMaterial(m)}
          onOpenSds={(m) => setSdsMaterial(m)}
          onBulkImport={() => setBulkOpen(true)}
          onTemplate={() => setTemplateConfirmOpen(true)}
          onInventoryCount={onOpenInventoryCount}
          onSdsBook={() => setSdsBookOpen(true)}
          cleanup={cleanup}
          deletions={deletions}
          canPermanentDeleteMaterial={canPermanentDeleteMaterial}
          canDeactivateModel={canDeactivateModel}
          recipes={recipes.recipes}
          recipesLoading={recipes.loading}
          recipeInventoryRows={recipeInventory.rows}
          recipeInventoryTotalValue={recipeInventory.totalInventoryValue}
          recipeInventoryLoading={recipeInventory.loading}
          recipeInventoryError={recipeInventory.error}
          onRenameRecipe={cleanup.renameRecipe}
          onDeactivateRecipe={cleanup.deactivateRecipe}
          receive={receive}
          receiptsAttention={receiptsAttention}
          onOpenPurchasing={onOpenPurchasing}
          onRefreshMaterials={materials.refresh}
          onRefreshRecipeInventory={recipeInventory.refresh}
          setImportToast={setImportToast}
          setRecipeToast={setRecipeToast}
        />
      ) : null}

      <ManufacturingInventoryPanelModals
        openModal={openModal}
        setOpenModal={(v) => (v === null ? closeModal() : setOpenModal(v))}
        editingMaterial={editingMaterial}
        existingCategories={categories.filter((c) => c !== "all")}
        existingUnits={existingUnits}
        existingShippingUnits={existingShippingUnits}
        canEditCode={canEditCatalogCode}
        onCreateMaterial={handleCreate}
        onUpdateMaterial={updateMaterial.submit}
        bulkOpen={bulkOpen}
        onBulkImportClose={() => setBulkOpen(false)}
        existingMaterials={materials.materials}
        onBulkImported={(summary) => {
          const parts = [`Imported ${summary.created} new`];
          if (summary.updated > 0) parts.push(`${summary.updated} updated`);
          if (summary.seededCostEntries > 0) parts.push(`${summary.seededCostEntries} seeded`);
          if (summary.skipped.length > 0) parts.push(`${summary.skipped.length} skipped`);
          const createFail = summary.createFailures?.length ?? 0;
          if (createFail > 0) {
            parts.push(`${createFail} code collision${createFail === 1 ? "" : "s"} — fix manually`);
          }
          setImportToast(parts.join(", "));
          materials.refresh();
        }}
        templateConfirmOpen={templateConfirmOpen}
        onTemplateCancel={() => setTemplateConfirmOpen(false)}
        onTemplateConfirmed={() => setTemplateConfirmOpen(false)}
        deletionPendingCopy={deletions.pendingCopy}
        {...deletions.softModal}
        {...deletions.permanentModal}
      />
      <ManufacturingInventoryPanelHosts
        enabled={gate.enabled}
        sdsMaterial={sdsMaterial}
        onSdsClose={() => setSdsMaterial(null)}
        sdsBookOpen={sdsBookOpen}
        onSdsBookClose={() => setSdsBookOpen(false)}
      />
      {stockMaterial ? (
        <ManufacturingInventoryMaterialDrawer
          material={stockMaterial}
          onClose={() => setStockMaterial(null)}
          onChanged={() => materials.refresh()}
        />
      ) : null}
    </div>
  );
}
